import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import { Course } from '../types';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { firestoreCache, CACHE_KEYS } from '../utils/firestoreCache';
import { collection, doc, setDoc, deleteDoc, query, where, getDocs, writeBatch, getDoc, addDoc } from 'firebase/firestore';
import { Platform } from 'react-native';

// 保存されたスケジュールテンプレートの型定義
export interface ScheduleTemplate {
  id: string;
  name: string;
  courses: Course[];
  createdAt: number;
  updatedAt: number;
}

// スケジュールコンテキストの型定義
export type AddCourseErrorType = 'already_added' | 'term_mismatch' | 'unknown';

export type TermMismatchType = 'none' | 'year' | 'semester' | 'both';

export interface ScheduleContextType {
  userCourses: Course[];
  addCourse: (course: Course) => Promise<{ success: boolean; conflictCourse?: Course; errorType?: AddCourseErrorType }>;
  removeCourse: (courseId: string) => Promise<void>;
  isCourseAdded: (courseId: string, academicYear?: string) => boolean;
  getConflictCourse: (dayOfWeek: number, period: number) => Course | undefined;
  hasTimeConflict: (dayOfWeek: number, period: number) => boolean;
  isTermMismatch: (course: Course) => TermMismatchType;

  // テンプレート管理
  saveCurrentAsTemplate: (name: string) => Promise<void>;
  savedTemplates: ScheduleTemplate[];
  loadTemplate: (templateId: string) => Promise<void>;
  deleteTemplate: (templateId: string) => Promise<void>;
  reorderTemplates: (fromIndex: number, toIndex: number) => Promise<void>;
  updateCourse: (courseId: string, updatedData: Partial<Course>) => Promise<void>;
  clearAllCourses: () => Promise<void>;
  isLoading: boolean;
  favoriteCustomCourses: Course[];
  addFavoriteCustomCourse: (course: Course) => Promise<void>;
  removeFavoriteCustomCourse: (courseId: string) => Promise<void>;

  // シラバス授業のお気に入り管理
  favoriteCourses: Course[];
  addFavoriteCourse: (course: Course) => Promise<void>;
  removeFavoriteCourse: (courseId: string, academicYear?: string) => Promise<void>;
  isFavoriteCourse: (courseId: string, academicYear?: string) => boolean;
}

// デフォルト値
const defaultScheduleContext: ScheduleContextType = {
  userCourses: [],
  addCourse: async () => ({ success: false, errorType: 'unknown' }),
  removeCourse: async () => { },
  isCourseAdded: () => false,
  getConflictCourse: () => undefined,
  hasTimeConflict: () => false,
  isTermMismatch: () => 'none',
  saveCurrentAsTemplate: async () => { },
  savedTemplates: [],
  loadTemplate: async () => { },
  deleteTemplate: async () => { },
  reorderTemplates: async () => { },
  updateCourse: async () => { },
  clearAllCourses: async () => { },
  isLoading: true,
  favoriteCustomCourses: [],
  addFavoriteCustomCourse: async () => { },
  removeFavoriteCustomCourse: async () => { },
  favoriteCourses: [],
  addFavoriteCourse: async () => { },
  removeFavoriteCourse: async () => { },
  isFavoriteCourse: (courseId: string, academicYear?: string) => false,
};

// コンテキストの作成
export const ScheduleContext = createContext<ScheduleContextType>(defaultScheduleContext);

// コンテキストプロバイダーの型定義
interface ScheduleProviderProps {
  children: ReactNode;
}

// コンテキストプロバイダーの実装
export const ScheduleProvider: React.FC<ScheduleProviderProps> = ({ children }) => {
  const [userCourses, setUserCourses] = useState<Course[]>([]);
  const [savedTemplates, setSavedTemplates] = useState<ScheduleTemplate[]>([]);
  const [favoriteCustomCourses, setFavoriteCustomCourses] = useState<Course[]>([]);
  const [favoriteCourses, setFavoriteCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadedScheduleUid, setLoadedScheduleUid] = useState<string | null>(null);
  const initialization = useRef<Promise<void>>(Promise.resolve());
  const favoritesLoading = useRef<Promise<void>>(Promise.resolve());
  const favoriteOperations = useRef<Promise<void>>(Promise.resolve());
  const favoritesRef = useRef<Course[]>([]);
  const customFavoritesRef = useRef<Course[]>([]);
  const favoriteDocuments = useRef(new Map<string, string[]>());
  const customFavoriteDocuments = useRef(new Map<string, string[]>());
  const favoritesRemoteLoaded = useRef({ courses: false, custom: false });
  const previousUserUid = useRef<string | null>(null);

  // AsyncStorageキー
  const STORAGE_KEY = '@userCourses';
  const TEMPLATES_KEY = '@savedTemplates';
  const FAVORITES_KEY = '@favoriteCustomCourses';
  const FAVORITE_COURSES_KEY = '@favoriteCourses';
  const FAVORITES_OWNER_KEY = '@favoritesLegacyOwner';
  const favoriteCacheKey = (custom: boolean, uid?: string) => `${custom ? FAVORITES_KEY : FAVORITE_COURSES_KEY}:${uid || 'guest'}`;

  // Web環境用のストレージヘルパー
  const storage = {
    getItem: async (key: string) => {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          return Promise.resolve(localStorage.getItem(key));
        }
        return AsyncStorage.getItem(key);
      } catch (error) {
        console.error('[Storage] getItem error:', error);
        return AsyncStorage.getItem(key);
      }
    },
    setItem: async (key: string, value: string) => {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem(key, value);
          console.log('[Storage] Saved to localStorage:', key, value.length, 'chars');
          return Promise.resolve();
        }
        console.log('[Storage] Saved to AsyncStorage:', key, value.length, 'chars');
        return AsyncStorage.setItem(key, value);
      } catch (error) {
        console.error('[Storage] setItem error:', error);
        return AsyncStorage.setItem(key, value);
      }
    },
    multiRemove: async (keys: string[]) => {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          keys.forEach(key => {
            console.log('[Storage] Removing from localStorage:', key);
            localStorage.removeItem(key);
          });
          return Promise.resolve();
        }
        return AsyncStorage.multiRemove(keys);
      } catch (error) {
        console.error('[Storage] multiRemove error:', error);
        return AsyncStorage.multiRemove(keys);
      }
    }
  };

  const readLocalFavorites = async (uid?: string) => {
    const scope = uid || 'guest';
    const [courses, custom, legacyOwner] = await Promise.all([
      storage.getItem(favoriteCacheKey(false, uid)), storage.getItem(favoriteCacheKey(true, uid)),
      storage.getItem(FAVORITES_OWNER_KEY),
    ]);
    let courseData = courses;
    let customData = custom;
    // Copy the old cache once; retain the original data and keep each account's cache separate.
    if ((!courses || !custom) && (!legacyOwner || legacyOwner === scope)) {
      const [legacyCourses, legacyCustom] = await Promise.all([
        storage.getItem(FAVORITE_COURSES_KEY), storage.getItem(FAVORITES_KEY),
      ]);
      if (!courses && legacyCourses) {
        courseData = legacyCourses;
        await storage.setItem(favoriteCacheKey(false, uid), legacyCourses);
      }
      if (!custom && legacyCustom) {
        customData = legacyCustom;
        await storage.setItem(favoriteCacheKey(true, uid), legacyCustom);
      }
      if (legacyCourses || legacyCustom) await storage.setItem(FAVORITES_OWNER_KEY, scope);
    }
    const parse = (raw: string | null): Course[] => {
      try {
        const data = raw ? JSON.parse(raw) : [];
        return Array.isArray(data) ? data.filter(course => course && typeof course.id === 'string') : [];
      } catch (error) {
        console.error('[ScheduleContext] Invalid favorites cache', error);
        return [];
      }
    };
    return { courses: parse(courseData), custom: parse(customData) };
  };

  // アプリ起動時の初期化
  useEffect(() => {
    const initializeApp = async () => {
      console.log('[ScheduleContext] App initializing...');
      
      // ローカルストレージからデータを読み込む（リロード時のデータ消失対策）
      try {
        const [localCourses, localTemplates] = await Promise.all([
          storage.getItem(STORAGE_KEY), storage.getItem(TEMPLATES_KEY),
        ]);
        
        if (localCourses) {
          const courses = JSON.parse(localCourses);
          console.log('[ScheduleContext] Loaded local courses:', courses.length);
          setUserCourses(courses);
        }
        
        if (localTemplates) {
          const templates = JSON.parse(localTemplates);
          console.log('[ScheduleContext] Loaded local templates:', templates.length);
          setSavedTemplates(templates);
        }
      } catch (error) {
        console.error('[ScheduleContext] Failed to load local data:', error);
      }
      try {
        const favorites = await readLocalFavorites(auth.currentUser?.uid);
        customFavoritesRef.current = favorites.custom;
        favoritesRef.current = favorites.courses;
        setFavoriteCustomCourses(favorites.custom);
        setFavoriteCourses(favorites.courses);
      } catch (error) {
        console.error('[ScheduleContext] Failed to load local favorites:', error);
      }
      
      setIsInitialized(true);
      setIsLoading(false);
    };

    initialization.current = initializeApp();
  }, []);

  const favoriteKey = (course: Course) => `${course.id}_${String(course.academicYear ?? '').replace(/[^0-9]/g, '') || 'unknown'}`;

  const loadFavorites = async (uid: string, isCurrent: () => boolean) => {
    await initialization.current;
    favoritesRemoteLoaded.current = { courses: false, custom: false };
    try {
      const cached = await readLocalFavorites(uid);
      if (!isCurrent()) return;
      favoritesRef.current = cached.courses;
      customFavoritesRef.current = cached.custom;
      setFavoriteCourses(cached.courses);
      setFavoriteCustomCourses(cached.custom);
    } catch (error) {
      console.error('[ScheduleContext] Failed to read favorites cache:', error);
    }
    const results = await Promise.allSettled([
      getDocs(query(collection(db, 'userFavoriteCourses'), where('userId', '==', uid))),
      getDocs(query(collection(db, 'userCustomCards'), where('userId', '==', uid))),
    ]);
    if (!isCurrent()) return;

    // Merge with the local copy so favorites saved while offline are preserved.
    for (const [index, result] of results.entries()) {
      if (result.status === 'rejected') {
        console.error('[ScheduleContext] Failed to restore favorites; keeping local data', result.reason);
        continue;
      }
      if (index === 0) favoritesRemoteLoaded.current.courses = true;
      else favoritesRemoteLoaded.current.custom = true;
      const current = index === 0 ? favoritesRef.current : customFavoritesRef.current;
      const keyOf = index === 0 ? favoriteKey : (course: Course) => course.id;
      const merged = new Map(current.map(course => [keyOf(course), course]));
      const documents = index === 0 ? favoriteDocuments.current : customFavoriteDocuments.current;
      documents.clear();
      result.value.forEach(snapshot => {
        const course = snapshot.data() as Course;
        if (!course.id) return;
        const key = keyOf(course);
        documents.set(key, [...(documents.get(key) || []), snapshot.id]);
        if (!merged.has(key)) merged.set(key, course);
      });
      const restored = Array.from(merged.values());
      if (index === 0) {
        favoritesRef.current = restored;
        setFavoriteCourses(restored);
      } else {
        customFavoritesRef.current = restored;
        setFavoriteCustomCourses(restored);
      }
      // Save restoration explicitly; no render effect may overwrite an unread favorites cache.
      await storage.setItem(favoriteCacheKey(index === 1, uid), JSON.stringify(restored)).catch(error => {
        console.error('[ScheduleContext] Failed to cache restored favorites', error);
      });
      if (!isCurrent()) return;
    }
  };

  // Serialize taps and wait for restoration so a late response cannot replace a new favorite.
  const changeFavorites = (operation: (uid?: string) => Promise<void>): Promise<void> => {
    const uid = auth.currentUser?.uid;
    const next = favoriteOperations.current.then(async () => {
      await initialization.current;
      await favoritesLoading.current;
      if (auth.currentUser?.uid !== uid) throw new Error('ログイン状態が変わりました。もう一度お試しください。');
      await operation(uid);
    });
    favoriteOperations.current = next.catch(() => undefined);
    return next;
  };

  useEffect(() => {
    let generation = 0;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('[ScheduleContext] Auth state changed:', user ? 'logged in' : 'logged out');
      
      setIsLoading(true);
      setLoadedScheduleUid(null);
      const currentGeneration = ++generation;
      const isCurrent = () => generation === currentGeneration;
      const oldUid = previousUserUid.current;
      previousUserUid.current = user?.uid || null;
      await initialization.current;
      if (!isCurrent()) return;

      if (oldUid && oldUid !== user?.uid) {
        favoritesRef.current = [];
        customFavoritesRef.current = [];
        setFavoriteCourses([]);
        setFavoriteCustomCourses([]);
        favoriteDocuments.current.clear();
        customFavoriteDocuments.current.clear();
      }
      favoritesLoading.current = user ? loadFavorites(user.uid, isCurrent) : Promise.resolve();
      
      if (user) {
        try {
          // ログイン時：Firestoreからデータ取得
          const userScheduleRef = doc(db, 'userSchedules', `${user.uid}_main`);
          const docSnap = await getDoc(userScheduleRef);
          if (!isCurrent()) return;

          if (docSnap.exists()) {
            const data = docSnap.data();
            const firestoreCourses = data.courses || [];
            
            console.log('[ScheduleContext] Loaded from Firestore:', firestoreCourses.length, 'courses');
            setUserCourses(firestoreCourses);
            await storage.setItem(STORAGE_KEY, JSON.stringify(firestoreCourses));
          } else {
            // Firestoreにデータがない場合、ローカルデータを確認
            const localData = await storage.getItem(STORAGE_KEY);
            const localCourses = localData ? JSON.parse(localData) : [];
            
            console.log('[ScheduleContext] No Firestore data, using local:', localCourses.length, 'courses');
            setUserCourses(localCourses);
            
            if (localCourses.length > 0) {
              await setDoc(userScheduleRef, {
                userId: user.uid,
                courses: localCourses,
                updatedAt: Date.now()
              });
            }
          }

          if (!isCurrent()) return;
          setLoadedScheduleUid(user.uid);

          // 他のデータも読み込み...
          console.log('[ScheduleContext] Loading templates from Firebase for user:', user.uid);
          const q = query(collection(db, 'userTemplates'), where('userId', '==', user.uid));
          const querySnapshot = await getDocs(q);
          if (!isCurrent()) return;
          const templates: ScheduleTemplate[] = [];
          querySnapshot.forEach((doc) => {
            templates.push({ id: doc.id, ...doc.data() } as ScheduleTemplate);
          });
          setSavedTemplates(templates);
          console.log('[ScheduleContext] Loaded templates from Firebase:', templates.length);

          // ローカルストレージにも保存（デプロイ時のデータ消失対策）
          await storage.setItem(TEMPLATES_KEY, JSON.stringify(templates));

        } catch (error) {
          console.error('[ScheduleContext] Failed to load user data', error);
        }
      } else if (oldUid) {
        // ログアウト時は表示をクリアし、ユーザー別のお気に入りキャッシュは保持する。
        console.log('[ScheduleContext] User logged out, clearing data');
        setUserCourses([]);
        setSavedTemplates([]);
        setFavoriteCustomCourses([]);
        setFavoriteCourses([]);
        favoritesRef.current = [];
        customFavoritesRef.current = [];
        
        // ローカルストレージもクリア
        await storage.multiRemove([STORAGE_KEY, TEMPLATES_KEY]);
        console.log('[ScheduleContext] Local storage cleared');
      }
      
      if (isCurrent()) setIsLoading(false);
    });

    return () => { generation++; unsubscribe(); };
  }, []);

  // userCoursesが変更されたらstorageとFirebaseに保存する
  useEffect(() => {
    const syncMainSchedule = async () => {
      if (!isInitialized) return;
      const uid = auth.currentUser?.uid;
      // Do not overwrite an account's saved schedule before its restore completes.
      if (uid && loadedScheduleUid !== uid) return;

      console.log('[ScheduleContext] syncMainSchedule triggered. Courses count:', userCourses.length);

      try {
        // Local Storage（常に保存）
        await storage.setItem(STORAGE_KEY, JSON.stringify(userCourses));

        // Firebase（ログイン時のみ）
        if (!uid || auth.currentUser?.uid !== uid) return;
        const userScheduleRef = doc(db, 'userSchedules', `${uid}_main`);
        await setDoc(userScheduleRef, {
          userId: uid,
          courses: userCourses,
          updatedAt: Date.now()
        });
        
        console.log('[ScheduleContext] Synced with Firestore. Courses saved:', userCourses.length);
      } catch (error) {
        console.error('[ScheduleContext] Failed to sync main schedule', error);
      }
    };

    // 即時実行（デバウンスなし）
    syncMainSchedule();
  }, [userCourses, isInitialized, loadedScheduleUid]);

  // savedTemplatesが変更されたらstorageに保存する
  useEffect(() => {
    if (!isInitialized) return;
    console.log('[ScheduleContext] Saving templates:', savedTemplates.length);
    storage.setItem(TEMPLATES_KEY, JSON.stringify(savedTemplates)).catch(err => {
      console.error('[ScheduleContext] Failed to save templates to storage', err);
    });
  }, [savedTemplates, isInitialized]);

  // リロード検知用のイベントリスナー
  useEffect(() => {
    if (!isInitialized || typeof window === 'undefined' || typeof document === 'undefined') return;
    const handleBeforeUnload = () => {
      console.log('[ScheduleContext] Page unloading, saving current state...');
      storage.setItem(STORAGE_KEY, JSON.stringify(userCourses));
      storage.setItem(TEMPLATES_KEY, JSON.stringify(savedTemplates));
    };

    // リロード時にも保存処理を実行
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        console.log('[ScheduleContext] Page hidden, saving state...');
        storage.setItem(STORAGE_KEY, JSON.stringify(userCourses));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [savedTemplates, userCourses, isInitialized]);

  // 時間帯の重複をチェック
  const getConflictCourse = (dayOfWeek: number, period: number): Course | undefined => {
    // オンデマンド授業(dayOfWeek === 0)は時間重複チェックをしない
    if (dayOfWeek === 0) return undefined;

    return userCourses.find(
      (course) => course.dayOfWeek === dayOfWeek && course.period === period
    );
  };

  // 時間帯に重複があるかどうかをチェック
  const hasTimeConflict = (dayOfWeek: number, period: number): boolean => {
    // オンデマンド授業(dayOfWeek === 0)は時間重複チェックをしない
    if (dayOfWeek === 0) return false;

    return userCourses.some(
      (course) => course.dayOfWeek === dayOfWeek && course.period === period
    );
  };

  // 年度・学期の不一致を詳細にチェック
  const isTermMismatch = (course: Course): TermMismatchType => {
    if (userCourses.length === 0 || course.isCustom) return 'none';

    const existingCourse = userCourses.find(c => !c.isCustom && (c.academicYear || c.semester));
    if (!existingCourse) return 'none';

    const normalizeYear = (y: any) => {
      if (y === undefined || y === null) return null;
      return y.toString().replace(/[^0-9]/g, '');
    };

    const normalizeSemester = (s: any) => {
      if (!s) return null;
      return s.toString().replace('学期', '');
    };

    const newYear = normalizeYear(course.academicYear);
    const existingYear = normalizeYear(existingCourse.academicYear);
    const newSemester = normalizeSemester(course.semester);
    const existingSemester = normalizeSemester(existingCourse.semester);

    const yearMismatch = newYear && existingYear && newYear !== existingYear;
    const semesterMismatch = newSemester && existingSemester && newSemester !== existingSemester;

    if (yearMismatch && semesterMismatch) return 'both';
    if (yearMismatch) return 'year';
    if (semesterMismatch) return 'semester';

    return 'none';
  };

  // 授業を追加
  const addCourse = async (course: Course): Promise<{ success: boolean; conflictCourse?: Course; errorType?: AddCourseErrorType }> => {
    console.log('[ScheduleContext] Attempting to add course:', course.id, course.name, 'Year:', course.academicYear, 'Semester:', course.semester);

    // すでに追加されている場合は何もしない
    if (isCourseAdded(course.id)) {
      return { success: false, errorType: 'already_added' };
    }

    // 既存の授業と年度・学期が一致するかチェック
    if (isTermMismatch(course) !== 'none') {
      console.warn('[ScheduleContext] Term mismatch detected for course:', course.name);
      return { success: false, errorType: 'term_mismatch' };
    }

    setUserCourses((prevCourses) => {
      // 登録時の名前と教室を initialName / initialRoom として保持
      const courseWithDefaults = {
        ...course,
        initialName: course.initialName || course.name,
        initialRoom: course.initialRoom || course.room
      };
      const newCourses = [...prevCourses, courseWithDefaults];
      return newCourses;
    });

    // Firebase (if logged in) - 履修状況の記録 (カスタム予定は除く)
    if (auth.currentUser && !course.isCustom) {
      try {
        const enrollmentId = `${course.id}_${auth.currentUser.uid}`;
        const enrollmentRef = doc(db, 'enrollments', enrollmentId);

        // すでに登録済みかチェック（二重カウント防止）
        const enrollmentSnap = await getDoc(enrollmentRef);
        if (!enrollmentSnap.exists()) {
          // enrollmentドキュメントを作成（バッチ集計のソースとなる）
          await setDoc(enrollmentRef, {
            courseId: course.id,
            userId: auth.currentUser.uid,
            createdAt: Date.now()
          });
          firestoreCache.invalidate(CACHE_KEYS.ENROLLMENT_COUNTS);
          firestoreCache.invalidate(CACHE_KEYS.courseEnrollmentCount(course.id));
          console.log('[ScheduleContext] Enrollment recorded in Firestore:', course.id);
        }
      } catch (error) {
        console.error('[ScheduleContext] Failed to record enrollment in Firestore', error);
      }
    }

    console.log('[ScheduleContext] Course added locally:', course.id);
    return { success: true };
  };

  // 授業を削除
  const removeCourse = async (courseId: string): Promise<void> => {
    // 削除するコースを取得してカスタムかどうか判定する
    const courseToRemove = userCourses.find(c => c.id === courseId);

    setUserCourses((prevCourses) => prevCourses.filter((course) => course.id !== courseId));

    // Firebase (if logged in) - 履修状況の削除 (カスタム予定は除く)
    if (auth.currentUser && courseToRemove && !courseToRemove.isCustom) {
      try {
        const enrollmentId = `${courseId}_${auth.currentUser.uid}`;
        const enrollmentRef = doc(db, 'enrollments', enrollmentId);

        const enrollmentSnap = await getDoc(enrollmentRef);
        if (enrollmentSnap.exists()) {
          const enrollmentData = enrollmentSnap.data();
          // 履歴コレクションにコピー
          await addDoc(collection(db, 'enrollmentHistory'), {
            courseId: enrollmentData.courseId,
            userId: enrollmentData.userId,
            createdAt: enrollmentData.createdAt,
            removedAt: Date.now(),
          });
          // enrollmentドキュメントを削除
          await deleteDoc(enrollmentRef);
          firestoreCache.invalidate(CACHE_KEYS.ENROLLMENT_COUNTS);
          firestoreCache.invalidate(CACHE_KEYS.courseEnrollmentCount(courseId));
          console.log('[ScheduleContext] Enrollment record removed from Firestore and saved to history:', courseId);
        }
      } catch (error) {
        console.error('[ScheduleContext] Failed to remove enrollment record from Firestore', error);
      }
    }
  };

  // 授業を更新
  const updateCourse = async (courseId: string, updatedData: Partial<Course>): Promise<void> => {
    setUserCourses((prevCourses) =>
      prevCourses.map((course) =>
        course.id === courseId ? { ...course, ...updatedData } : course
      )
    );
  };

  // すべての授業をリセット
  const clearAllCourses = async (): Promise<void> => {
    console.log('[ScheduleContext] clearAllCourses: Resetting timetable...');

    const coursesToClear = [...userCourses];
    if (coursesToClear.length === 0) return;

    // 1. UIを即座にリセット（Optimistic Update）して素早く画面から消す
    setUserCourses([]);

    // 2. 同期の遅延や競合を防ぐため明示的に Local Storage もクリア
    try {
      await storage.setItem(STORAGE_KEY, JSON.stringify([]));
    } catch (err) {
      console.error('[ScheduleContext] clearAllCourses: AsyncStorage error', err);
    }

    // 3. Firebase（ログイン中のみ）の情報を一括削除（バッチ処理）
    if (auth.currentUser) {
      try {
        const batch = writeBatch(db);

        // enrollment レポートの削除 (カスタム予定は除く)
        for (const course of coursesToClear) {
          if (!course.isCustom) {
            const enrollmentId = `${course.id}_${auth.currentUser.uid}`;
            const enrollmentRef = doc(db, 'enrollments', enrollmentId);
            const enrollmentSnap = await getDoc(enrollmentRef);
            if (enrollmentSnap.exists()) {
              const enrollmentData = enrollmentSnap.data();
              // 履歴コレクションにコピー
              await addDoc(collection(db, 'enrollmentHistory'), {
                courseId: enrollmentData.courseId,
                userId: enrollmentData.userId,
                createdAt: enrollmentData.createdAt,
                removedAt: Date.now(),
              });
            }
            batch.delete(enrollmentRef);
          }
        }

        // メイン時間割も明示的に空として保存
        const userScheduleRef = doc(db, 'userSchedules', `${auth.currentUser.uid}_main`);
        batch.set(userScheduleRef, {
          userId: auth.currentUser.uid,
          courses: [],
          updatedAt: Date.now()
        });

        await batch.commit();
        firestoreCache.invalidate(CACHE_KEYS.ENROLLMENT_COUNTS);
        for (const course of coursesToClear) {
          if (!course.isCustom) {
            firestoreCache.invalidate(CACHE_KEYS.courseEnrollmentCount(course.id));
          }
        }
        console.log('[ScheduleContext] clearAllCourses: Enrollment records saved to history and schedule removed from Firestore in a single batch');
      } catch (error) {
        console.error('[ScheduleContext] clearAllCourses: Failed to clear records in Firestore', error);
      }
    }
  };

  // 現在の時間割をテンプレートとして保存
  const saveCurrentAsTemplate = async (name: string): Promise<void> => {
    console.log('[ScheduleContext] Saving template:', name, 'Courses:', userCourses.length);
    
    const newTemplate: ScheduleTemplate = {
      id: Math.random().toString(36).substr(2, 9),
      name,
      courses: [...userCourses],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    const newTemplates = [...savedTemplates, newTemplate];
    setSavedTemplates(newTemplates);
    
    // まずローカルストレージに保存
    await storage.setItem(TEMPLATES_KEY, JSON.stringify(newTemplates));
    console.log('[ScheduleContext] Template saved to local storage:', newTemplate.name);

    if (auth.currentUser) {
      try {
        console.log('[ScheduleContext] Saving to Firebase:', newTemplate.id, 'User:', auth.currentUser.uid);
        await setDoc(doc(db, 'userTemplates', newTemplate.id), {
          ...newTemplate,
          userId: auth.currentUser.uid,
        });
        console.log('[ScheduleContext] Template saved to Firebase:', newTemplate.name);
      } catch (error) {
        console.error('[ScheduleContext] Failed to save template to Firebase', error);
      }
    } else {
      console.warn('[ScheduleContext] No authenticated user, template saved locally only');
    }
  };

  // カスタム予定をお気に入りに追加
  const addFavoriteCustomCourse = (course: Course): Promise<void> => changeFavorites(async (uid) => {
    // 既に同じ名前・場所のものがお気に入りにあるかチェック
    const isAlreadyFavorited = customFavoritesRef.current.some(c => c.name === course.name && c.room === course.room);
    if (isAlreadyFavorited) return;

    // IDはそのまま使う（上書きしない）
    const newFavorites = [...customFavoritesRef.current, course];
    await storage.setItem(favoriteCacheKey(true, uid), JSON.stringify(newFavorites));
    if (auth.currentUser?.uid !== uid) throw new Error('ログイン状態が変わりました。もう一度お試しください。');
    customFavoritesRef.current = newFavorites;
    setFavoriteCustomCourses(newFavorites);

    if (uid) {
      try {
        const documentId = `${uid}_${course.id}`;
        await setDoc(doc(db, 'userCustomCards', documentId), {
          ...JSON.parse(JSON.stringify(course)),
          userId: uid,
        });
        if (auth.currentUser?.uid === uid) {
          customFavoriteDocuments.current.set(course.id, [...new Set([
            ...(customFavoriteDocuments.current.get(course.id) || []), documentId,
          ])]);
        }
      } catch (error) {
        console.error('[ScheduleContext] Failed to save custom card to Firebase', error);
      }
    }
  });

  // カスタム予定をお気に入りから削除
  const removeFavoriteCustomCourse = (courseId: string): Promise<void> => changeFavorites(async (uid) => {
    if (uid && !favoritesRemoteLoaded.current.custom) throw new Error('保存済みカードを確認できませんでした。通信状態を確認してください。');
    const previous = customFavoritesRef.current;
    const newFavorites = previous.filter(c => c.id !== courseId);
    await storage.setItem(favoriteCacheKey(true, uid), JSON.stringify(newFavorites));
    if (auth.currentUser?.uid !== uid) throw new Error('ログイン状態が変わりました。もう一度お試しください。');
    customFavoritesRef.current = newFavorites;
    setFavoriteCustomCourses(newFavorites);

    if (uid) {
      try {
        const ids = new Set(customFavoriteDocuments.current.get(courseId) || []);
        await Promise.all(Array.from(ids, id => deleteDoc(doc(db, 'userCustomCards', id))));
        if (auth.currentUser?.uid === uid) customFavoriteDocuments.current.delete(courseId);
      } catch (error) {
        console.error('[ScheduleContext] Failed to delete custom card from Firebase', error);
        await storage.setItem(favoriteCacheKey(true, uid), JSON.stringify(previous));
        if (auth.currentUser?.uid === uid) {
          customFavoritesRef.current = previous;
          setFavoriteCustomCourses(previous);
        }
        throw error;
      }
    }
  });

  // シラバス授業をお気に入りに追加
  const addFavoriteCourse = (course: Course): Promise<void> => changeFavorites(async (uid) => {
    const isAlreadyFavorited = favoritesRef.current.some(saved => favoriteKey(saved) === favoriteKey(course));
    if (isAlreadyFavorited) return;

    const newFavorites = [...favoritesRef.current, course];
    await storage.setItem(favoriteCacheKey(false, uid), JSON.stringify(newFavorites));
    if (auth.currentUser?.uid !== uid) throw new Error('ログイン状態が変わりました。もう一度お試しください。');
    favoritesRef.current = newFavorites;
    setFavoriteCourses(newFavorites);

    if (uid) {
      try {
        const key = favoriteKey(course);
        const docId = `${uid}_${key}`;

        await setDoc(doc(db, 'userFavoriteCourses', docId), {
          ...JSON.parse(JSON.stringify(course)),
          userId: uid,
        });
        if (auth.currentUser?.uid === uid) {
          favoriteDocuments.current.set(key, [...new Set([
            ...(favoriteDocuments.current.get(key) || []), docId,
          ])]);
        }
      } catch (error) {
        console.error('[ScheduleContext] Failed to save favorite course to Firebase', error);
      }
    }
  });

  // シラバス授業をお気に入りから削除
  const removeFavoriteCourse = (courseId: string, academicYear?: string): Promise<void> => changeFavorites(async (uid) => {
    if (uid && !favoritesRemoteLoaded.current.courses) throw new Error('保存済みのお気に入りを確認できませんでした。通信状態を確認してください。');
    const normalizeYear = (y: any) => y === undefined || y === null ? null : y.toString().replace(/[^0-9]/g, '');
    const searchYear = normalizeYear(academicYear);

    const previous = favoritesRef.current;
    const newFavorites = previous.filter(course => {
      if (searchYear && course.academicYear) {
        return !(course.id === courseId && normalizeYear(course.academicYear) === searchYear);
      }
      return course.id !== courseId;
    });

    await storage.setItem(favoriteCacheKey(false, uid), JSON.stringify(newFavorites));
    if (auth.currentUser?.uid !== uid) throw new Error('ログイン状態が変わりました。もう一度お試しください。');
    favoritesRef.current = newFavorites;
    setFavoriteCourses(newFavorites);

    if (uid) {
      try {
        const removedKeys = previous.filter(course => !newFavorites.includes(course)).map(favoriteKey);
        const ids = new Set(removedKeys.flatMap(key => favoriteDocuments.current.get(key) || []));
        await Promise.all(Array.from(ids, id => deleteDoc(doc(db, 'userFavoriteCourses', id))));
        if (auth.currentUser?.uid === uid) removedKeys.forEach(key => favoriteDocuments.current.delete(key));
      } catch (error) {
        console.error('[ScheduleContext] Failed to delete favorite course from Firebase', error);
        await storage.setItem(favoriteCacheKey(false, uid), JSON.stringify(previous));
        if (auth.currentUser?.uid === uid) {
          favoritesRef.current = previous;
          setFavoriteCourses(previous);
        }
        throw error;
      }
    }
  });

  // シラバス授業がお気に入りに登録されているかチェック
  const isFavoriteCourse = (courseId: string, academicYear?: string): boolean => {
    return favoriteCourses.some((course) => {
      if (academicYear && course.academicYear) {
        const normalizeYear = (y: any) => {
          if (y === undefined || y === null) return null;
          return y.toString().replace(/[^0-9]/g, '');
        };
        const searchYear = normalizeYear(academicYear);
        const addedYear = normalizeYear(course.academicYear);
        return course.id === courseId && searchYear === addedYear;
      }
      return course.id === courseId;
    });
  };

  // テンプレートを読み込む
  const loadTemplate = async (templateId: string): Promise<void> => {
    const template = savedTemplates.find(t => t.id === templateId);
    if (template) {
      setUserCourses(template.courses);
    }
  };

  // テンプレートを削除
  const deleteTemplate = async (templateId: string): Promise<void> => {
    const newTemplates = savedTemplates.filter(t => t.id !== templateId);
    setSavedTemplates(newTemplates);
    await storage.setItem(TEMPLATES_KEY, JSON.stringify(newTemplates));

    // Firebase (if logged in)
    if (auth.currentUser) {
      try {
        await deleteDoc(doc(db, 'userTemplates', templateId));
      } catch (error) {
        console.error('[ScheduleContext] Failed to delete template from Firebase', error);
      }
    }
  };

  // テンプレートの順序を変更
  const reorderTemplates = async (fromIndex: number, toIndex: number): Promise<void> => {
    if (fromIndex === toIndex) return;
    const newTemplates = [...savedTemplates];
    const [moved] = newTemplates.splice(fromIndex, 1);
    newTemplates.splice(toIndex, 0, moved);
    setSavedTemplates(newTemplates);
    await storage.setItem(TEMPLATES_KEY, JSON.stringify(newTemplates));
  };

  // 授業が追加されているかチェック
  const isCourseAdded = (courseId: string, academicYear?: string): boolean => {
    return userCourses.some((course) => {
      // カスタム予定の場合はIDだけで判定
      if (course.isCustom) return course.id === courseId;

      // シラバス授業の場合は、同じID（シラバスコード）でも年度が違えば別物として扱う
      if (academicYear && course.academicYear) {
        const normalizeYear = (y: any) => {
          if (y === undefined || y === null) return null;
          return y.toString().replace(/[^0-9]/g, '');
        };
        const searchYear = normalizeYear(academicYear);
        const addedYear = normalizeYear(course.academicYear);
        return course.id === courseId && searchYear === addedYear;
      }
      return course.id === courseId;
    });
  };

  return (
    <ScheduleContext.Provider
      value={{
        userCourses,
        addCourse,
        removeCourse,
        isCourseAdded,
        getConflictCourse,
        hasTimeConflict,
        isTermMismatch,
        saveCurrentAsTemplate,
        savedTemplates,
        loadTemplate,
        deleteTemplate,
        reorderTemplates,
        updateCourse,
        clearAllCourses,
        isLoading,
        favoriteCustomCourses,
        addFavoriteCustomCourse,
        removeFavoriteCustomCourse,
        favoriteCourses,
        addFavoriteCourse,
        removeFavoriteCourse,
        isFavoriteCourse,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};

// カスタムフック
export const useSchedule = () => useContext(ScheduleContext);

export default ScheduleContext;
